import { useEffect, useState } from "react";
import { apiPost } from "../../client/apiClient.js";
import type { ApiResult, BuildingDetails, PriceBreakdown, ServiceSelection } from "../../shared/types.js";
import { IncludedList } from "../../components/Sections.js";
import { LoadingState } from "../../components/States.js";
import { ShieldIcon } from "../../components/Icons.js";

function formatEuro(cents: number): string {
  return (cents / 100).toLocaleString("nl-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function usePriceBreakdown(building: BuildingDetails, service: ServiceSelection, postalCode?: string) {
  const [breakdown, setBreakdown] = useState<PriceBreakdown | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = (await apiPost("/api/price/calculate", { building, service, postalCode })) as ApiResult<PriceBreakdown>;
        if (cancelled) return;
        if (res.ok) {
          setBreakdown(res.data);
          setError(null);
        } else {
          setError(res.error);
        }
      } catch {
        if (!cancelled) setError("Prijs kon niet worden berekend.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(building), JSON.stringify(service), postalCode]);

  return { breakdown, loading, error };
}

export function PriceSummaryPanel({ breakdown, loading }: { breakdown: PriceBreakdown | null; loading: boolean }) {
  return (
    <aside className="card card-pad price-summary" aria-label="Prijsindicatie">
      <h3 style={{ marginBottom: "var(--space-4)" }}>Uw prijsindicatie</h3>
      {loading && !breakdown && <LoadingState label="Prijs wordt berekend…" />}
      {breakdown && breakdown.attestNotRequired && (
        <p className="text-small">
          <strong>Geen asbestattest nodig.</strong> {breakdown.attestNotRequiredReason}
        </p>
      )}
      {breakdown && !breakdown.attestNotRequired && breakdown.requiresCustomQuote && (
        <p className="text-small">{breakdown.customQuoteReason}</p>
      )}
      {breakdown && !breakdown.attestNotRequired && !breakdown.requiresCustomQuote && (
        <>
          {breakdown.lineItems.map((li) => (
            <div className="price-summary__line" key={li.id}>
              <span>{li.label}</span>
              <span>&euro;{formatEuro(li.amountExclVat)}</span>
            </div>
          ))}
          <div className="price-summary__line">
            <span>Btw ({breakdown.vatRatePercent}%)</span>
            <span>&euro;{formatEuro(breakdown.vatCents)}</span>
          </div>
          {breakdown.contributionCents !== 0 && (
            <div className="price-summary__line">
              <span>{breakdown.contributionLabel}</span>
              <span>{breakdown.contributionCents < 0 ? "-" : "+"}&euro;{formatEuro(Math.abs(breakdown.contributionCents))}</span>
            </div>
          )}
          <div className="price-summary__total">
            <span>Totaal incl. btw</span>
            <span className="price-summary__total-value">&euro;{formatEuro(breakdown.totalInclVatCents)}</span>
          </div>
          <IncludedList notes={breakdown.includedNotes} />
        </>
      )}
      <p className="text-tiny text-muted mt-4 mb-0">
        <ShieldIcon size={12} /> Bij uitzonderlijke situaties kan een aangepaste offerte nodig zijn — dat melden we
        altijd vooraf, nooit achteraf.
      </p>
    </aside>
  );
}

export function MobileStickyPrice({ breakdown, visible }: { breakdown: PriceBreakdown | null; visible: boolean }) {
  return (
    <div className="mobile-sticky-price" data-visible={visible ? "true" : "false"}>
      <div>
        <div className="text-tiny text-muted">Totaal incl. btw</div>
        <div className="mobile-sticky-price__value">
          {breakdown && !breakdown.requiresCustomQuote && !breakdown.attestNotRequired
            ? `€${formatEuro(breakdown.totalInclVatCents)}`
            : "—"}
        </div>
      </div>
    </div>
  );
}
